import { useEffect, useRef, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { ChatMessages, ChatInput, readSseStream } from "./chat/ChatParts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const sid = (() => { let s = localStorage.getItem("nura_chat_sid"); if (!s) { s = "chat-" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36); localStorage.setItem("nura_chat_sid", s); } return s; })();
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
const appendToLast = (delta) => (m) => { const c = [...m]; const last = c[c.length - 1]; c[c.length - 1] = { ...last, content: last.content + delta }; return c; };

export const ChatWidget = () => {
  const { lang, track, visitorId, config, consent } = useStore();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottom = useRef(null);
  const loaded = useRef(false);
  const fr = lang === "fr";
  const hidden = config.chat_enabled === false || (config.cookie_banner_enabled && !consent?.chat);

  useEffect(() => {
    if (!open) return;
    track("chat_open");
    if (loaded.current) return;
    loaded.current = true;
    fetch(`${API}/chat/${sid}`).then((r) => r.json()).then((h) => setMsgs(h.map((m) => ({ id: m.id || newId(), role: m.role, content: m.content })))).catch((e) => console.warn("chat history unavailable", e?.message));
  }, [open, track]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { id: newId(), role: "user", content: text }, { id: newId(), role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid, message: text, lang, visitor_id: visitorId }) });
      if (!res.ok) throw new Error(`chat ${res.status}`);
      await readSseStream(res, (delta) => setMsgs(appendToLast(delta)), () => setMsgs(appendToLast(fr ? " (erreur, réessayez)" : " (error, retry)")));
    } catch (err) {
      console.warn("chat request failed", err?.message);
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: fr ? "Désolé, une erreur est survenue." : "Sorry, something went wrong." }; return c; });
    } finally { setBusy(false); }
  };

  if (hidden) return null;
  return (
    <>
      <button data-testid="chat-toggle" onClick={() => setOpen(!open)} aria-label="Chat"
        className="fixed bottom-5 right-5 z-40 w-13 h-13 p-4 rounded-full bg-stone-900 text-[#FAF7F2] shadow-xl hover:bg-[#C85A32] transition-colors">
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>
      {open && (
        <div className="fixed bottom-24 right-5 z-40 w-[min(380px,calc(100vw-2.5rem))] h-[520px] max-h-[70vh] rounded-2xl bg-white border border-stone-200 shadow-2xl flex flex-col overflow-hidden" data-testid="chat-window">
          <div className="bg-[#1C1917] text-[#EFEAE1] px-5 py-4">
            <p className="font-serif tracking-[0.2em]">NŪRA</p>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#EFEAE1]/50">{fr ? "Assistant · répond en quelques secondes" : "Assistant · replies in seconds"}</p>
          </div>
          <ChatMessages msgs={msgs} fr={fr} bottomRef={bottom} />
          <ChatInput input={input} setInput={setInput} onSend={send} busy={busy} fr={fr} />
        </div>
      )}
    </>
  );
};
